/**
 * Feature Flag Service - Manages feature flags and module enablement
 * Supports runtime toggling and database persistence
 */

import { supabase } from '../utils/supabaseClient';
import { MODULE_REGISTRY, getModulesForDeployment } from '../config/moduleRegistry';

class FeatureFlagService {
  constructor() {
    this.flags = {};
    this.enabledModules = new Set();
    this.initialized = false;
  }

  /**
   * Initialize feature flags from database or defaults
   */
  async initialize(companyId, deploymentType = 'beta') {
    try {
      // ✅ FIXED: Use .maybeSingle() instead of .single() to handle empty result gracefully
      // .maybeSingle() returns null if no rows found, instead of throwing PGRST116 error
      const { data: flagsRes, error } = await supabase
        .from('feature_flags')
        .select('*')
        .eq('company_id', companyId)
        .maybeSingle();

      if (error) {
        // Error occurred (other than no data)
        console.warn('Error loading feature flags:', error.message);
        this.flags = this.getDefaultFlags(deploymentType);
      } else if (flagsRes) {
        // Record found - use it
        this.flags = flagsRes.flags || {};
      } else {
        // No record found - use defaults
        this.flags = this.getDefaultFlags(deploymentType);
      }

      // Enable modules based on flags
      this.updateEnabledModules();
      this.initialized = true;
    } catch (e) {
      console.warn('Failed to initialize feature flags:', e.message);
      // Fall back to defaults
      this.flags = this.getDefaultFlags(deploymentType);
      this.updateEnabledModules();
      this.initialized = true;
    }
  }

  /**
   * Get default flags based on deployment type
   */
  getDefaultFlags(deploymentType = 'beta') {
    const defaults = {};
    const modules = getModulesForDeployment(deploymentType);
    
    modules.forEach(module => {
      defaults[module.id] = true;
    });

    return defaults;
  }

  /**
   * Update enabled modules based on current flags
   */
  updateEnabledModules() {
    this.enabledModules.clear();
    Object.entries(this.flags).forEach(([moduleId, enabled]) => {
      if (enabled) {
        this.enabledModules.add(moduleId);
      }
    });
  }

  /**
   * Check if a module is enabled
   */
  isModuleEnabled(moduleId) {
    return this.enabledModules.has(moduleId);
  }

  /**
   * Check if a page is accessible (module enabled)
   */
  isPageEnabled(pageName) {
    // Find which module owns this page
    for (const module of Object.values(MODULE_REGISTRY)) {
      if (module.pages.includes(pageName)) {
        return this.isModuleEnabled(module.id);
      }
    }
    return false;
  }

  /**
   * Get all enabled modules
   */
  getEnabledModules() {
    return Array.from(this.enabledModules);
  }

  /**
   * Toggle a feature flag (admin only)
   */
  async toggleFeature(companyId, moduleId, enabled) {
    try {
      this.flags[moduleId] = enabled;
      this.updateEnabledModules();

      // Persist to database using Supabase client
      const { data: existingFlags } = await supabase
        .from('feature_flags')
        .select('id')
        .eq('company_id', companyId)
        .single();

      if (existingFlags) {
        // Update existing
        const { error } = await supabase
          .from('feature_flags')
          .update({ flags: this.flags })
          .eq('id', existingFlags.id);

        if (error) throw error;
      } else {
        // Create new
        const { error } = await supabase
          .from('feature_flags')
          .insert({ company_id: companyId, flags: this.flags });

        if (error) throw error;
      }

      return true;
    } catch (e) {
      console.error('Failed to toggle feature:', e);
      return false;
    }
  }

  /**
   * Get feature flag value
   */
  getFlag(moduleId) {
    return this.flags[moduleId] ?? false;
  }

  /**
   * Set multiple flags at once
   */
  async setFlags(companyId, flagsObject) {
    try {
      this.flags = { ...this.flags, ...flagsObject };
      this.updateEnabledModules();

      // Persist to database using Supabase client
      const { data: existingFlags } = await supabase
        .from('feature_flags')
        .select('id')
        .eq('company_id', companyId)
        .single();

      if (existingFlags) {
        const { error } = await supabase
          .from('feature_flags')
          .update({ flags: this.flags })
          .eq('id', existingFlags.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('feature_flags')
          .insert({ company_id: companyId, flags: this.flags });

        if (error) throw error;
      }

      return true;
    } catch (e) {
      console.error('Failed to set flags:', e);
      return false;
    }
  }
}

export default new FeatureFlagService();

